<template>
  <div class="nonAppMenu" :class="{ footer: !isHeader }" ref="thisMenu">
    <div class="menuItem" v-if="isMobile && !isHeader">
      <router-link aria-label="Home" :to="{ name: 'Home' }" class="home" title="Home">
        <div class="selInd">
          <BrandIcon v-show="isHomeRoute" name="flat/home-solid" :showFocus="false" />
          <BrandIcon v-show="!isHomeRoute" name="flat/home-compact" :showFocus="false" />
        </div>
      </router-link>
    </div>
    <button id="triggerHelp-header" class="menuItem triggerHelp" :title="`Get help with ${$appName}`"
            v-if="showHelpIcon">
      <BrandIcon name="flat/help-compact" class="icon help" :heavy="true" />
    </button>
    <div class="menuItem" v-if="!isMobileHeader">
      <button aria-label="Search" @click.stop="openSearchOverlay(currentLocation)">
        <BrandIcon name="flat/search-compact" title="Search" :heavy="true"></BrandIcon>
      </button>
    </div>
    <div v-if="!isMobile" class="menuItem privacy">
      <button @click="togglePrivacyMode"
              :aria-label="`${privacyMode ? 'Disable' : 'Enable'} privacy mode`"
              :title="`${privacyMode ? 'Disable' : 'Enable'} privacy mode`">
        <BrandIcon
          class="icon"
          :heavy="true"
          :focused="privacyMode"
          :class="{ highlighted: privacyMode }"
          :name="privacyMode ? 'flat/privacy-solid' : 'flat/privacy-compact'" />
      </button>
    </div>
    <template v-if="!isMobileHeader">
      <div class="menuItem notificationWrapper" :class="{selectedPage}">
        <NotificationsPopup
          :above="isMobile"
          @clearAllNotifications="$emit('clearAllNotifications')">
          <div
            ref="notificationIcon" class="notificationIcon" :class="{active: !!numNotifications}"
            :title="displayNotificationCount(numNotifications)">
            <BrandIcon class="icon" name="flat/notifications-compact" />
            <NotificationDot :count="formattedNotificationCount" v-if="numNotifications"></NotificationDot>
          </div>
        </NotificationsPopup>
      </div>
      <div class="menuItem" v-if="extraMenuItems.length" :class="{ selected: overflowMenuItemSelected }">
        <PopupMenu
          :items="extraMenuItems"
          horizontalPosition="left"
          :verticalPosition="isMobile ? 'above' : 'below'">
          <template #menuOpener>
            <div class="overflowOpener">
              <BrandIcon v-if="overflowMenuItemSelected" :name="selectedOverflowMenuItem.icon"
                         :title="selectedOverflowMenuItem.name" />
              <BrandIcon name="flat/ellipsis-vertical-compact" title="More options" />
            </div>
          </template>
        </PopupMenu>
      </div>
    </template>
    <div class="menuItem_ profile" v-if="!isMobile || isHeader">
      <ProfileContextMenu
        @userStatusClicked="userStatusClicked"
        @toggleAppearOfflineClicked="toggleAppearOfflineClicked"
        @showCustomDoNotDisturbDialog="showCustomDoNotDisturbDialog">
        <div title="Open profile menu" class="userProfile">
          <div v-if="!isMobile" class="userDisplayName" v-text="userDisplayName"></div>
          <UserStatusIcon
            v-if="!isMobile && currentUserStatus"
            class="userStatusEmoji"
            :emoji="currentUserStatus.emoji"
            :text="currentUserStatus.text">
          </UserStatusIcon>
          <UserIdent ref="userIdent" class="ident" :user="currentUser" :showOnlineStatus="true" />
        </div>
      </ProfileContextMenu>
    </div>
    <template v-if="isOverlayOpen('search')">
      <div class="searchOverlay" :class="isMobile ? 'mobile' : ''">
        <div class="background" @click.stop="closeSearchOverlay"></div>
        <div class="background2" v-if="isMobile"></div>
        <div class="searchPadding before"></div>
        <SearchEntryComponent @close="closeSearchOverlay" class="searchEntry"
                              @searchTextChanged="searchTerm = $event" :searchLocation="searchOverlayLocation"
                              @click.stop>
        </SearchEntryComponent>
        <div class="searchPadding after"></div>
      </div>
    </template>
    <UserStatusDialog v-if="showUserStatusDialog" @close="closeUserStatusDialog" />
    <DoNotDisturbUntilCustomDialog v-if="displayCustomDoNotDisturbDialog" @save="hideCustomDoNotDisturbDialog"
                                   @close="hideCustomDoNotDisturbDialog" />
  </div>
</template>

<script setup lang="ts">
import {
  ComponentPublicInstance,
  computed,
  ComputedRef,
  defineAsyncComponent,
  onMounted,
  onUnmounted,
  Ref,
  ref,
  watch,
} from 'vue';
import {
  RouteLocationNamedRaw,
  RouteLocationNormalized,
  RouteRecordName,
} from 'vue-router';

import PopupMenu from '@/components/dialogs/popupmenu/PopupMenu.vue';
import { PopupMenuItem } from '@/components/dialogs/popupmenu/PopupMenuItem';
import BrandIcon from '@/components/icons/BrandIcon.vue';
import NotificationDot from '@/components/NotificationDot.vue';
import NotificationsPopup from '@/components/NotificationsPopup.vue';
import UserIdent from '@/components/UserIdent.vue';
import { useNotifications } from '@/composables/notification/Notifications';
import { useRoute } from '@/composables/router/VueRouter';
import BrowserDetection from '@/data/BrowserDetection';
import { SearchLocation } from '@/data/datatypes/SemanticSearchQuery';
import { SystemSettingsPage } from '@/data/datatypes/Settings';
import { FullUserDetails } from '@/data/datatypes/UserDetails';
import { UserStatus } from '@/data/datatypes/UserStatus';
import Features from '@/data/Features';
import { formatNotificationCount } from '@/data/helpers/TrackHelper';
import appEventBus from '@/eventbus/EventBus';
import ProfileContextMenu from '@/ProfileContextMenu.vue';
import RouteNames from '@/router/RouteNames';
import { useUIStateStore } from '@/stores/UIState';
import { useUserStore } from '@/stores/User';
import UserStatusIcon from '@/UserStatusIcon.vue';

const SearchEntryComponent = defineAsyncComponent(() => import('@/components/search/SearchEntryComponent.vue'));
const UserStatusDialog = defineAsyncComponent(() => import('@/UserStatusDialog.vue'));
const DoNotDisturbUntilCustomDialog = defineAsyncComponent(
  () => import('@/components/user/DoNotDisturbUntilCustomDialog.vue'));

const userStore = useUserStore();
const uiStateStore = useUIStateStore();

const route = useRoute();

const { allNotifications } = useNotifications();

/** Names of the available overlay menus */
const overlayNames = ['profileMenu', 'search', 'notification', 'appsMenu'] as const;
type OverlayName = typeof overlayNames[number];

/** Type for tracking the open state for each overlay (i.e. `true` if it's open; `false` if it's closed) */
type OverlayOpenStates = {
  [overlay in OverlayName]: { open: boolean, x: number | null, y: number | null };
}

interface Props {
  isHeader?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  isHeader: true,
});

interface Emits {
  (e: 'clearAllNotifications'): void,
}
defineEmits<Emits>();

const userIdent: Ref<ComponentPublicInstance | null> = ref(null);
const thisMenu: Ref<HTMLDivElement | null> = ref(null);
const notificationIcon: Ref<HTMLDivElement | null> = ref(null);

const searchTerm: Ref<string> = ref('');
const searchOverlayLocation: Ref<SearchLocation> = ref(SearchLocation.ALL);
const headerBottom: Ref<number> = ref(0);
const privacyMode: Ref<boolean> = ref(false);
const currentRoute: Ref<RouteLocationNormalized> = ref({} as unknown as RouteLocationNormalized);
const showUserStatusDialog: Ref<boolean> = ref(false);
const displayCustomDoNotDisturbDialog: Ref<boolean> = ref(false);
const overlayOpenStates: Ref<OverlayOpenStates> = ref({
  appsMenu: { open: false, x: -1, y: -1 },
  notification: { open: false, x: -1, y: -1 },
  profileMenu: { open: false, x: -1, y: -1 },
  search: { open: false, x: -1, y: -1 },
});

function isOverlayOpen(overlayName: OverlayName): boolean {
  return (overlayOpenStates.value[overlayName]?.open &&
  (overlayOpenStates.value[overlayName]?.x ?? -1) > -1 &&
  (overlayOpenStates.value[overlayName]?.y ?? -1) > -1);
}

function closeOverlays(...overlayNames: OverlayName[]): void {
  overlayNames.forEach(name => {
    overlayOpenStates.value[name] = { open: false, x: 0, y: 0 };
  });
}

function openOverlays(...overlayNames: OverlayName[]): void {
  overlayNames.forEach(name => {
    openOverlay(name);
  });
}

function openOverlay(overlayName: OverlayName): void {
  let x: number = 0;
  let y: number = 0;

  const headerRect = thisMenu.value?.getBoundingClientRect();
  if (headerRect) {
    headerBottom.value = headerRect.bottom;
    y = headerRect.bottom;

    if (isMobile.value) {
      y = 1;
    } else {
      y -= 8;
    }
  }

  switch (overlayName) {
    case 'profileMenu': {
      const avatarRect = userIdent.value?.$el.getBoundingClientRect();
      if (avatarRect) {
        x = avatarRect.right;
        if (!isMobile.value) {
          // On desktop, move the edge to the centre of the avatar
          x -= avatarRect.width / 2;
        }
      }
      break;
    }
    case 'search': {
      // Search isn't a menu, so doesn't use the PopupMenu component and is handled differently
      break;
    }
    case 'notification': {
      const buttonRect = notificationIcon.value?.getBoundingClientRect();
      if (buttonRect) {
        // Push this to the right a bit to look more connected in the UI
        x = buttonRect.right + (buttonRect.width / 2);
      }
      break;
    }
    case 'appsMenu': {
      break;
    }
  }

  closeAllOverlaysExcept(overlayName);
  overlayOpenStates.value[overlayName].x = x;
  overlayOpenStates.value[overlayName].y = y;
  overlayOpenStates.value[overlayName].open = true;
}

/**
 * Close all overlays, except for the specified one
 * @param overlayName the {@link OverlayName} to keep open
 * @private
 */
function closeAllOverlaysExcept(overlayName: OverlayName): void {
  const toClose: OverlayName[] = [];
  for (const overlay of overlayNames) {
    if (overlay !== overlayName) {
      // OverlayName type is defined from overlayNames; we can cast it
      toClose.push(overlay as OverlayName);
    }
  }

  closeOverlays(...toClose);
}

function closeUserStatusDialog(): void {
  showUserStatusDialog.value = false;
}

function userStatusClicked(): void {
  showUserStatusDialog.value = true;
}

const currentUser: ComputedRef<FullUserDetails> = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return userStore.currentUser!;
});

const currentUserStatus: ComputedRef<UserStatus | null> = computed(() => {
  return userStore.currentUserStatus;
});

const userDisplayName: ComputedRef<string | null> = computed(() => {
  return userStore.userDisplayName;
});

const isHomeRoute: ComputedRef<boolean> = computed(() => {
  return route.name === RouteNames.HOME_ROUTE_NAME;
});

const isMobile: ComputedRef<boolean> = computed(() => {
  return BrowserDetection.isMobile();
});

const isMobileHeader: ComputedRef<boolean> = computed(() => {
  return props.isHeader && isMobile.value;
});

const numNotifications: ComputedRef<number> = computed(() => {
  return allNotifications.value.length;
});

const formattedNotificationCount: ComputedRef<string | null> = computed(() => {
  return formatNotificationCount(numNotifications.value);
});

const selectedPage: ComputedRef<boolean> = computed(() => {
  return route.name === RouteNames.NOTIFICATIONS_ROUTE_NAME;
});

const showHelpIcon: ComputedRef<boolean> = computed(() => {
  return !!((window as unknown) as { pendo: unknown }).pendo && !isMobile.value;
});

function openSearchOverlay(location: SearchLocation = currentLocation.value): void {
  searchOverlayLocation.value = location;
  openOverlays('search');
}

const currentLocation: ComputedRef<SearchLocation> = computed(() => {
  return uiStateStore.searchMenuOverlayLocation;
});

const extraMenuItems: ComputedRef<PopupMenuItem[]> = computed(() => {
  const items: PopupMenuItem[] = [];
  if (shouldShowKBItem.value) {
    addOverflowMenuItem(items, 'Knowledge management', 'flat/book-compact',
      RouteNames.KNOWLEDGE_MANAGEMENT_ROUTE_NAME);
  }
  if (shouldShowWorkspacePublishingItem.value) {
    // TODO: correct icon
    addOverflowMenuItem(items, 'Publisher', 'flat/upload-compact',
      RouteNames.PUBLISH_TEMPLATES_ROUTE_NAME);
  }
  if (userStore.isReportingUser) {
    addOverflowMenuItem(items, 'Reporting', 'flat/stats-compact',
      RouteNames.REPORTING_ROUTE_NAME);
  }
  if (userStore.isUserTenantAdmin) {
    addOverflowMenuItem(items, 'Tenant settings', 'flat/settings-compact',
      RouteNames.TENANT_SETTINGS_ROUTE_NAME, { tenantId: 'me', page: 'details' });
  }
  if (userStore.isUserSystemAdmin) {
    // TODO: correct icon
    addOverflowMenuItem(items, 'System settings', 'flat/sliders-vertical-compact',
      RouteNames.SYSTEM_SETTINGS_ROUTE_NAME, { page: SystemSettingsPage.SYSTEM });
  }

  return items;
});

const shouldShowWorkspacePublishingItem: ComputedRef<boolean> = computed(() => {
  return userStore.isUserFeatureEnabled(Features.PUBLISH_ROOMS) && userStore.isUserTenantAdmin;
});

// visible if feature toggle on, and wiki exists
const shouldShowKBItem: ComputedRef<boolean> = computed(() => {
  return userStore.isUserFeatureEnabled(Features.KNOWLEDGE_BASE) &&
    !!userStore.currentTokenIfValid &&
    !!userStore.currentTokenIfValid.kbWikiId &&
    userStore.currentTokenIfValid.kbWikiId.length > 0;
});

const selectedOverflowMenuItem: ComputedRef<PopupMenuItem | undefined> = computed(() => {
  if (!currentRoute.value || !extraMenuItems.value?.length) {
    return;
  }
  return extraMenuItems.value.find((item: PopupMenuItem) => {
    if (!item.route) {
      return false;
    }
    let routeName: RouteRecordName | undefined;
    if (typeof item.route === 'string') {
      routeName = item.route as string;
    } else {
      routeName = (item.route as RouteLocationNamedRaw).name;
    }
    if (!routeName) {
      return false;
    }
    return routeNameMatchesCurrentRoute(routeName);
  });
});

function routeNameMatchesCurrentRoute(routeName: RouteRecordName): boolean {
  // This is a bit hacky, it's because the reporting routes are all defined at the same level.
  if (routeName === RouteNames.REPORTING_ROUTE_NAME) {
    const currentRouteName: RouteRecordName | null | undefined = currentRoute.value.name;
    return currentRouteName === routeName ||
      currentRouteName === RouteNames.REPORTING_TENANT_ROUTE_NAME;
  }
  return currentRoute.value.matched.some((routeRecord) => {
    return routeRecord.name === routeName;
  });
}

const overflowMenuItemSelected: ComputedRef<boolean> = computed(() => {
  return !!selectedOverflowMenuItem.value;
});

function addOverflowMenuItem(items: PopupMenuItem[], name: string, icon: string, routeName: string,
  routeParams?: Record<string, string>): void {
  let cssClass: string | undefined;
  if (routeNameMatchesCurrentRoute(routeName)) {
    icon = icon.replace('-light', '').replace('-compact', '') + '-solid';
    cssClass = 'selected';
  }
  items.push(new PopupMenuItem('item',
    name,
    {
      optionsType: 'router',
      route: { name: routeName, params: routeParams },
      icon,
      cssClass,
    }
  ));
}

function closeSearchOverlay(): void {
  searchOverlayLocation.value = SearchLocation.ALL;
  closeOverlays('search');
}

function togglePrivacyMode(): void {
  privacyMode.value = !privacyMode.value;
}

function toggleAppearOfflineClicked(): void {
  currentUser.value.appearOffline = !currentUser.value.appearOffline;
  userStore.updateCurrentUser(currentUser.value);
}

function showCustomDoNotDisturbDialog(): void {
  displayCustomDoNotDisturbDialog.value = true;
}

function hideCustomDoNotDisturbDialog(): void {
  displayCustomDoNotDisturbDialog.value = false;
}

function displayNotificationCount(num: number): string {
  if (num === 1) {
    return '1 notification';
  }
  return `${num} notifications`;
}

watch(() => privacyMode.value, (newValue: boolean) => {
  uiStateStore.setPrivacyMode(newValue);
});

watch(() => route, (newValue: RouteLocationNormalized) => {
  // Set via a watcher as it wasn't reliably recalculating when using directly in a getter
  currentRoute.value = newValue;
}, { immediate: true });

onMounted(() => {
  appEventBus.on('openSearch', handleOpenSearch);
});

onUnmounted(() => {
  appEventBus.off('openSearch', handleOpenSearch);
});

function handleOpenSearch(event: {location: SearchLocation}): void {
  openSearchOverlay(event.location);
}
</script>

<style scoped lang="scss">
$icon-line-height: 1.8rem;

.nonAppMenu {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  height: 100%;
  position: relative;
  align-items: center;
  gap: 0.875rem;
  padding-right: 0.5rem;
  // This is needed to prevent any occlusion on the focus outline
  margin-left: 2px;

  .mobile & {
    padding-right: 0;
    justify-content: space-around;
    gap: 0;
  }
}

.userStatusEmoji {
  font-size: 1rem;
}

.mobileWrapperGroup {
  display: flex;
  flex: 1 1 auto;
  overflow: hidden;
  height: 100%;
  max-width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  position: relative;

  .brandIconContainer {
    margin-top: 0.125rem;
  }

  button {
    justify-content: flex-start;
    overflow: hidden;
    &.back {
      flex: 0 0 1.5rem;
    }
    &.title {
      flex: 1 1;
      margin-left: 0.5rem;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: flex-start;
      border-radius: 0;
      overflow: hidden;
    }
  }

  .toggle {
    color: $colour-text-primary;
    font-size: $size-font-small;
  }
}
.mobileHeader {
  font-size: 1rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.group {
  display: flex;
  flex: 1 1 33%;
  height: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  .mobile & {
    flex: 1 1 auto;

    &:first-child {
      flex: 0 0 auto;
    }
  }

  &:first-child {
    justify-content: flex-start;
    .mobile & {
      // flex: 0 0 2.75rem;
      overflow: hidden;

      :deep(.brandIconContainer) {
        margin-left: -0.4rem;
      }
    }
  }

  &:last-child {
    flex: 0 0 auto;
    justify-content: flex-end;
    gap: 0.75rem;
    padding-right: 0.5rem;
  }
}

.prefixIcon {
  line-height: $icon-line-height;
  margin-top: 0.1rem;
}

.searchBar {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  color: $colour-input-search-placeholder;
  border: 1px solid $colour-border-mid;
  border-radius: 1rem;
  background-color: $colour-background-light;
  padding: 0 0.5rem;
  padding-right: 1rem;
  margin: 0 0.5rem;
  cursor: text;
  height: 2rem;
  flex: 0 1 36.75rem;

  .icon {
    width: 1.5rem;
    color: $colour-icon-primary;
    margin-right: 0.25rem;
  }

  .placeholder {
    font-weight: $weight-semi-bold;
  }
}

.icon {
  &.help {
    width: 2rem;
    height: 2rem;
  }
}

.notificationWrapper {
  user-select: none;
  border-radius: 0.5rem;
  overflow: hidden;

  &.selectedPage {
    background-color: $colour-background-light;
  }

  .notificationIcon {
    cursor: pointer;
    color: $colour-icon-primary;
    height: 28px;
    min-width: 2rem;
    width: fit-content;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 0 0 auto;
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .prefixIcon {
      font-size: 1rem;
      width: 100%;
      text-align: center;
      display: inline-block;
      flex: 0 0 14px;
    }

    .notificationDotWrapper {
      position: absolute;
      top: 50%;
      right: 0;
      transform: translate(-10%, -100%);
    }
  }
}

.userProfile {
  margin: 0 0 0 0.5rem;
  font-weight: $weight-semi-bold;
  display: flex;
  gap: 0.25rem;
  align-items: center;
  cursor: pointer;
  justify-content: flex-start;
  user-select: none;
  color: $colour-text-primary;

  // Following two rules are for browsers without focus-visible support
  :focus & {
    outline: 2px solid $colour-border-emphasis-highlight;
    outline-offset: 4px;
    border-radius: 0.25rem;
  }

  :focus:not(:focus-visible) & {
    outline: none;
  }

  :focus-visible & {
    outline: 2px solid $colour-border-emphasis-highlight;
    outline-offset: 4px;
    border-radius: 0.25rem;
  }

  .userIdent {
    margin-left: 0.5rem;
  }

  .mobile & {
    margin-right: 0;
    margin-left: 0;
    height: 2.5rem;
    justify-content: center;
  }

  .userIcon {
    width: 2.5rem;
    min-width: 2.5rem;
    height: 2.5rem;
    font-size: 1rem;
    border-radius: 50%;
    color: white;
    overflow: hidden;
    position: relative;

    .userInitials {
      width: 100%;
      height: 100%;
      display:inline-block;
      line-height: 2rem;
      text-align: center;
      text-transform: uppercase;
    }

    .userAvatar {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      height: 100%;
      width: 100%;
      object-fit: cover;
      background-color: $colour-background-light;
    }
  }

  .userDisplayName {
    max-width: 8rem;
    max-height: 1.5rem;
    vertical-align: middle;
    position: relative;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: $weight-semi-bold;
    font-size: 1rem;

    div {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      // An extra 20% on font size for leading is usually the minimum to avoid cutting off descenders
      line-height: 1.2;
    }
  }
}

.searchOverlay {
  position: fixed;
  top: 0.875rem;
  z-index: 1;
  &.mobile {
    top: 3.5rem;
    .searchEntry {
      border-radius: 0;
    }
    // bottom: 4rem;
    .background {
      background-color: transparent;
    }
  }
  .background {
    background-color: $colour-border-dark;
    opacity: 0.2;
  }
  left: 0;
  width: 100%;
  display: flex;
  .searchPadding {
    flex: 1 1;

    &.after {
      flex: 1 1;
    }
  }
  .searchEntry {
    flex: 0 1 36.75rem;
    pointer-events: all;
  }
  pointer-events: none;
}
.privacy {
  .icon {
    width: 2rem;
    height: 2rem;
    &.highlighted {
      color: $color-menu-highlight;
    }
  }
}

.background {
  position: fixed;
  top:0;
  left:0;
  width: 100%;
  height: 100%;
  z-index: -1;
  pointer-events: all;
}
.background2 {
  position: fixed;
  top: 3.375rem;
  left:0;
  background-color: $colour-background-light;
  width: 100%;
  height: calc(100% - 7.5rem);
  z-index: -1;
  pointer-events: none;
}

.ident {
  font-size: 1rem;
  margin-left: 0.5rem;
  .mobile & {
    font-size: inherit;
  }
}
.triggerHelp.placeholder {
  // if we don't make it visible, then pendo will ignore it.
  width: 1px;
  height: 1px;
  pointer-events: none;
}
.logo {
  max-height: 100%;
  max-width: 100%;
  height: 100%
}
.menuItem {
  padding: 2px;
  border-radius: 0;
  margin: 0 -2px;
  .mobile & {
    flex: 0 0 20%;
    display: flex;
    align-items: center;
    justify-content: center;

    &.profile {
      margin-right: 0.5rem;
    }
  }

  button:focus-visible, a:focus-visible, .popupMenuContainer :deep(button:focus-visible) {
    outline: 2px solid $colour-border-emphasis-highlight;
    outline-offset: 0;
    border-radius: 0.25rem;
  }

  a {
    display: block;
    border: none;
  }
  .brandIconContainer {
    width: 1.75rem;
    height: 1.75rem;
    color: $do-not-use-outside-this-file-grey-50pc; // TODO: var

    .mobile & {
      width: 2rem;
      height: 2rem;
    }
  }
  &.selected, :not(.home).router-link-active, .router-link-exact-active, :deep(.selected) {
    .brandIconContainer {
      color: $color-menu-highlight;
    }
  }
  .activeIcon {
    display: none;
  }
  .router-link-active {
    .activeIcon {
      display: block;
    }
    .inactiveIcon {
      display: none;
    }
  }
}
.overflowOpener {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
